import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Container,
  Grid,
  Avatar,
  Button,
  TextField,
  Link,
  Box,
  Typography, CircularProgress, IconButton
} from '@mui/material'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import ToggleNotice from './ToggleNotice'

import {
  login,
  clearIsSuccess,
  clearIsLoading,
  secondLogin,
  sendSecond
} from '@store/user'
import PropTypes from 'prop-types'

const CircularProgressWithLabel = (props) => {
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <CircularProgress variant="determinate" value={props.value} />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {
          props.isTimeOver ?
            <IconButton aria-label="reSend" onClick={props.reSendCode}>
              <RefreshIcon/>
            </IconButton> :
            <Typography variant="caption" component="div" color="text.secondary">
              {`${180 - props.time}s`}
            </Typography>
        }
      </Box>
    </Box>
  )
}

CircularProgressWithLabel.propTypes = {
  /**
   * The value of the progress indicator for the determinate variant.
   * Value between 0 and 100.
   * @default 0
   */
  value: PropTypes.number.isRequired,
}

const Login = () => {
  const dispatch = useDispatch()
  const [isExternal] = useState(window.location.href.indexOf("kakaobank.com") > -1)
  const { isLoading, isSuccess, tempUser } = useSelector(store => store.user)
  const [progress, setProgress] = useState(0)
  const [time, setTime] = useState(0)
  const [isTimeOver, setIsTimeOver] = useState(false)
  const [isFail, setIsFail] = useState(false)
  const [noticeType, setNoticeType] = useState('Y') // 정/부 선택

  useEffect(() => {
    clearIsLoading()

    if (!isTimeOver && isSuccess) {
      const timer = setInterval(() => {
        setTime(prevTime => {
          const tempTime = prevTime >= 180 ? 180 : prevTime + 1
          setProgress(100 - tempTime / 1.8)

          if (tempTime >= 180) {
            setIsTimeOver(true)
          }

          return tempTime
        })
      }, 1000)

      return () => {
        clearInterval(timer)
      }
    }
  }, [isTimeOver, isSuccess])

  useEffect(() => {
    if (isSuccess) {
      clearSendState()
      //call sendSMS api
      const sendSMS = async () => {
        await dispatch(sendSecond({
          userId: tempUser.userId,
          password: tempUser.password,
          mainManager: noticeType === 'Y',
        }))
      }

      sendSMS()
        .catch(console.error)
    }

    return () => {
      clearIsSuccess()
    }
    // eslint-disable-next-line
  }, [dispatch, isSuccess])

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (isLoading) {
      return
    }

    const data = new FormData(event.currentTarget)

    if (isSuccess) {
      const result = await dispatch(secondLogin({
        userId: tempUser.userId,
        password: tempUser.password,
        secondCode: data.get('secondCode')
      }))

      if (!result.payload) {
        setIsFail(true)
      }
    } else {
      await dispatch(login({
        userId: data.get('username'),
        password: data.get('password')
      }))

      clearSendState()
    }
  }

  const reSendCode = async () => {
    await dispatch(sendSecond({
      userId: tempUser.userId,
      password: tempUser.password
    }))

    clearSendState()
  }

  const clearSendState = () => {
    setTime(0)
    setProgress(0)
    setIsTimeOver(false)
  }

  const onChangeNoticeType = (e, value) => {
    if (value !== null) {
      setNoticeType(value)
    }
  }

  return (
    <Container component="div">
      <Container component="div" maxWidth="md">
        <Box
          sx={{
            marginTop: 15,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Avatar sx={{ m: 1 }}>
            <LockOutlinedIcon />
          </Avatar>
          <br />
          <Typography component="h1" variant="h4">
            카카오뱅크 특수관계인 변동 사항 등록 시스템
          </Typography>
          <br /><br />
        </Box>
      </Container>
      <Container component="div" maxWidth="xs">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  id="username"
                  label="아이디"
                  name="username"
                  autoFocus
                  inputProps={{
                    maxLength: 50
                  }}
                  disabled={isSuccess}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  name="password"
                  label="비밀번호"
                  type="password"
                  id="password"
                  inputProps={{
                    maxLength: 100
                  }}
                  disabled={isSuccess}
                />
              </Grid>

              <ToggleNotice 
                noticeType={noticeType}
                onChangeNoticeType={onChangeNoticeType}
              />

              {
                isSuccess &&
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    error={isFail}
                    name="secondCode"
                    label="인증코드"
                    id="secondCode"
                    autoFocus={true}
                    helperText="알림톡으로 전송된 인증코드를 입력 후 인증 버튼을 눌러주세요."
                    inputProps={{
                      maxLength: 20
                    }}
                    InputProps={{
                      endAdornment: (
                        <CircularProgressWithLabel value={progress} time={time} isTimeOver={isTimeOver} reSendCode={reSendCode}/>
                      )
                    }}
                  />
                </Grid>
              }
            </Grid>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isLoading || isTimeOver}
            >
              {
                isSuccess ? isLoading ? '인증 중입니다...' : '인증' :
                isLoading ? '로그인 중입니다...' : '로그인'
              }
            </Button>
            <Grid container justifyContent="flex-end">
              <Grid item>
                <Link href={isExternal ? 'https://ac.kabang.io' : 'https://iam.kabang.io'} variant="body2">
                  로그인이 안되는 경우 관리자에게 문의주세요.
                </Link>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Container>
      <Copyright sx={{ mt: 5 }} />
    </Container>
  )
}

const Copyright = (props) => {
  return (
    <Typography variant="body2" color="text.secondary" align="center" {...props}>
      Copyright © KakaoBank of Korea Corp. All rights reserved.
    </Typography>
  )
}

export default Login